import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "abj_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.slug === product.slug);
      if (idx === -1) {
        return [...prev, {
          slug: product.slug,
          title: product.title,
          price: product.price,
          image: product.image,
          type: product.type,
          quantity,
        }];
      }
      const next = [...prev];
      next[idx] = { ...next[idx], quantity: Math.min(99, next[idx].quantity + quantity) };
      return next;
    });
  }, []);

  const setQuantity = useCallback((slug, quantity) => {
    setItems((prev) => {
      if (quantity <= 0) return prev.filter((p) => p.slug !== slug);
      return prev.map((p) => (p.slug === slug ? { ...p, quantity: Math.min(99, quantity) } : p));
    });
  }, []);

  const remove = useCallback((slug) => {
    setItems((prev) => prev.filter((p) => p.slug !== slug));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = items.reduce((n, i) => n + i.quantity, 0);
  const subtotal = items.reduce((n, i) => n + i.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, add, setQuantity, remove, clear, count, subtotal, open, setOpen }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
